import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { useOpenTickets } from '../context/OpenTicketsContext'
import { addPrintedIds, getAutoPrint, getPrintedIds } from '../utils/deviceId'
import { newLinesSince } from '../utils/mergeLines'
import { buildMoveSlip } from '../utils/moveSlip'
import { fetchMovedTickets } from '../services/kitchen'
import type { Order } from '../types'
import { isViewMode } from '../services/viewMode'
import type { ReceiptKind } from '../components/receipt/Receipt'

interface Options {
  /** Hands a slip to the shell, which renders and prints it. */
  onPrint: (order: Order, kind?: ReceiptKind) => void
  /** Called when a slip has been printed, so the till can badge the ticket. */
  onPrinted?: (id: string) => void
}

/**
 * Prints waiters' tickets at the till as they arrive.
 *
 * Lives at the shell rather than on the sale screen, because it used to unmount
 * the moment the cashier opened Ayarlar or Rapor, a waiter's order rung in
 * while the till was on another page simply never printed, and they would walk
 * up to a printer that had produced nothing.
 *
 * A browser can't reach a printer on another device, so the till does the
 * printing: the waiter's phone writes the ticket, this sees it and prints. It
 * works while the till has the app open on any page, which is the normal state
 * during service.
 */
export function useTicketPrinter({ onPrint, onPrinted }: Options) {
  const { canSettle, tenantId } = useAuth()
  const { openTickets, todayOrders } = useOpenTickets()
  const openRef = useRef(openTickets)
  openRef.current = openTickets

  /** Tickets already seen here. Null until the first snapshot lands. */
  const seenTickets = useRef<Set<string> | null>(null)
  /**
   * What each open ticket held the last time this till looked at it. A waiter's
   * later round is the difference against this, which is all that gets printed.
   */
  const sentLines = useRef<Map<string, Order['lines']>>(new Map())

  /**
   * window.print() blocks, so two tickets arriving together would collide.
   * They go through one at a time instead.
   */
  const printQueue = useRef<{ order: Order; kind?: ReceiptKind }[]>([])
  const printing = useRef(false)

  const drainPrintQueue = useCallback(() => {
    if (printing.current) return
    const item = printQueue.current.shift()
    if (!item) return
    const next = item.order

    printing.current = true
    onPrint(next, item.kind)
    // Recorded for every slip: this is the record of what this printer has
    // physically produced, and it is what stops a reload from sending the same
    // ticket through a second time.
    //
    // Addition slips are excluded. They carry a synthetic id that no ticket
    // will ever match, so recording them would spend a slot in a bounded store
    // and push real ticket ids out of it sooner.
    if (!next.id.includes('+')) {
      addPrintedIds([next.id])
      onPrinted?.(next.id)
    }
    // print() returns once the dialog closes; the gap lets the next receipt
    // render before it is asked to print.
    window.setTimeout(() => {
      printing.current = false
      drainPrintQueue()
    }, 1200)
  }, [onPrint, onPrinted])

  const queuePrint = useCallback(
    (tickets: Order[], kind?: ReceiptKind) => {
      printQueue.current.push(...tickets.map((order) => ({ order, kind })))
      drainPrintQueue()
    },
    [drainPrintQueue],
  )

  /** Her açık adisyonun son görülen masası; null ilk görüntüye kadar. */
  const tableOf = useRef<Map<string, string | undefined> | null>(null)
  /** Bakılmış birleştirmeler (iptal edilen kaynak adisyonlar). */
  const mergesSeen = useRef(new Set<string>())
  const mergesSeeded = useRef(false)

  /**
   * "MASA DEĞİŞTİ" fişi (23 Eki 2026). Adisyon taşınınca ya da birleşince
   * tetik mutfak fişlerine nereden geldiğini yazıyor; kasa onları okuyup
   * bekleyen ürünler için tezgaha kısa bir fiş basıyor. Kendi yaptığı taşıma
   * önce yerel kayıttan görünüyor, sunucu henüz yazmamış olabilir: birkaç kez
   * deneniyor. Basılan taşıma kalıcı kayda yazılıyor, yenilemede tekrar yok.
   */
  const checkMove = useCallback(
    (orderId: string) => {
      if (!tenantId) return
      const since = Date.now() - 2 * 60_000
      let tries = 0
      const run = async () => {
        tries += 1
        try {
          const order = openRef.current.find((t) => t.id === orderId)
          const tickets = order ? await fetchMovedTickets(tenantId, orderId, since) : []
          const slip = order ? buildMoveSlip(order, tickets) : null
          if (!slip) {
            if (tries < 4) window.setTimeout(() => void run(), 1500)
            return
          }
          const mark = `move:${slip.id}`
          if (getPrintedIds().has(mark)) return
          addPrintedIds([mark])
          if (getAutoPrint() && !isViewMode()) queuePrint([slip], 'move')
        } catch (err) {
          console.error('[yazıcı] masa değişti fişi', err)
        }
      }
      window.setTimeout(() => void run(), 800)
    },
    [tenantId, queuePrint],
  )

  useEffect(() => {
    if (!canSettle) return
    const tables = tableOf.current
    if (tables === null) {
      tableOf.current = new Map(openTickets.map((t) => [t.id, t.tableName]))
      return
    }
    const live = new Set<string>()
    for (const t of openTickets) {
      live.add(t.id)
      if (tables.has(t.id) && tables.get(t.id) !== t.tableName) checkMove(t.id)
      tables.set(t.id, t.tableName)
    }
    for (const id of tables.keys()) if (!live.has(id)) tables.delete(id)
  }, [canSettle, openTickets, checkMove])

  useEffect(() => {
    if (!canSettle || todayOrders.length === 0) return
    // İlk görüntüdeki birleştirmeler eski: yalnız kaydediliyor.
    const first = !mergesSeeded.current
    mergesSeeded.current = true
    for (const o of todayOrders) {
      if (o.status !== 'void' || !o.mergedInto || mergesSeen.current.has(o.id)) continue
      mergesSeen.current.add(o.id)
      if (!first && openTickets.some((t) => t.id === o.mergedInto)) checkMove(o.mergedInto)
    }
  }, [canSettle, todayOrders, openTickets, checkMove])

  /**
   * Marks a ticket's contents as already handled, without printing.
   *
   * The till's own additions print through the flow that made them; without
   * this claim the write would come back from the server and read as a waiter's
   * round, printing a second time.
   */
  const claim = useCallback((ticketId: string, lines: Order['lines']) => {
    sentLines.current.set(ticketId, lines)
  }, [])

  useEffect(() => {
    if (!canSettle) return

    const seen = seenTickets.current
    if (seen === null) {
      seenTickets.current = new Set(openTickets.map((t) => t.id))
      // Seeded with what each ticket already holds, so an addition made before
      // this till was watching does not read as a brand new round.
      for (const t of openTickets) sentLines.current.set(t.id, t.lines)
      return
    }

    // Tickets are recorded as seen whether or not printing is on. Skipping the
    // bookkeeping while it's off would make every ticket that arrived in the
    // meantime look pre-existing the moment someone enables it, so nothing
    // would print until the ticket after that.
    const fresh = openTickets.filter((t) => !seen.has(t.id) && !t.pending)
    for (const t of fresh) seen.add(t.id)

    // Checked against the stored record too, not just this session's set. The
    // first-snapshot guard above only holds while the page lives; a reload
    // rebuilds this from scratch and every unpaid ticket would look new again.
    const printed = getPrintedIds()
    const unprinted = fresh.filter((t) => !printed.has(t.id))

    /**
     * Rounds added to tickets the till has already printed.
     *
     * The document id does not change when a waiter adds to a table, so the
     * new-ticket check above cannot see it, the table would be served a second
     * round the kitchen never heard about. Only the difference is printed: the
     * rest of that ticket went to the stalls when it was first rung in.
     */
    const rounds: Order[] = []
    for (const t of openTickets) {
      if (t.pending || unprinted.includes(t)) continue
      // Saved deliberately without paper, on whichever device rang it in. Only
      // the writer knows that; this machine would otherwise read the same
      // document change as a waiter's order and print it.
      if (t.silent) continue
      const before = sentLines.current.get(t.id)
      if (!before) continue
      const added = newLinesSince(before, t.lines)
      if (added.lines.length === 0) continue
      rounds.push({
        ...t,
        lines: added.lines,
        itemCount: added.itemCount,
        totalKurus: added.totalKurus,
        // Synthetic, so the printed-slip store keeps treating the ticket's own
        // id as the thing that was printed.
        id: `${t.id}+${Date.now()}`,
        createdAtLocal: Date.now(),
      })
    }

    // Recorded whether or not printing is on, for the same reason as `seen`.
    //
    // Pending tickets are deliberately left alone. A waiter's round reaches
    // this screen through the local cache first, arriving `pending` and being
    // skipped above; recording it anyway would leave nothing to compare when
    // the server copy lands, and the round would never print.
    for (const t of openTickets) {
      if (!t.pending) sentLines.current.set(t.id, t.lines)
    }
    // Settled and cancelled tickets leave the list; dropping them keeps this
    // from growing for the length of the service.
    const live = new Set(openTickets.map((t) => t.id))
    for (const id of sentLines.current.keys()) {
      if (!live.has(id)) sentLines.current.delete(id)
    }

    const queue = [...unprinted, ...rounds]
    // Görüntüleme modunda (sistem yöneticisi) bu tarayıcı kurumun fişlerini basmıyor.
    if (queue.length > 0 && getAutoPrint() && !isViewMode()) queuePrint(queue)
  }, [canSettle, openTickets, queuePrint])

  /**
   * Yazıcısız cihazda yapılıp "kasada basılacak" işaretlenen satışlar (21 Eki
   * 2026, "Fişleri kasa bilgisayarından bas"). Kasada yapılmış gibi kasa fişi
   * olarak basılıyor (tezgah fişleri dahil). Bilgisayar kapalıyken birikenlerin
   * hepsi açılınca birden basılmasın: yalnız son 30 dakika. Basılanlar kalıcı
   * kayda (printedIds) yazılıyor, sayfa yenilenince tekrar basılmıyor.
   */
  useEffect(() => {
    if (!canSettle || !getAutoPrint() || isViewMode()) return
    const printed = getPrintedIds()
    const since = Date.now() - 30 * 60_000
    const due = todayOrders.filter(
      (o) =>
        o.printAtTill &&
        o.status === 'completed' &&
        !o.pending &&
        o.createdAtLocal >= since &&
        !printed.has(o.id) &&
        !printQueue.current.some((q) => q.order.id === o.id),
    )
    if (due.length > 0) queuePrint(due, 'cash')
  }, [canSettle, todayOrders, queuePrint])

  return { queuePrint, claim }
}
